import { City, Sector } from "@/types/mainTypes";
import {} from "lucide-react";
import * as Icons from "lucide-react";
import { ForwardRefExoticComponent, RefAttributes } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export enum AmenityEnum {
  WasherDryer = "WasherDryer",
  AirConditioning = "AirConditioning",
  Dishwasher = "Dishwasher",
  HighSpeedInternet = "HighSpeedInternet",
  HardwoodFloors = "HardwoodFloors",
  WalkInClosets = "WalkInClosets",
  Microwave = "Microwave",
  Refrigerator = "Refrigerator",
  Pool = "Pool",
  Gym = "Gym",
  Parking = "Parking",
  PetsAllowed = "PetsAllowed",
  WiFi = "WiFi",
}

export enum HighlightEnum {
  HighSpeedInternetAccess = "HighSpeedInternetAccess",
  WasherDryer = "WasherDryer",
  AirConditioning = "AirConditioning",
  Heating = "Heating",
  SmokeFree = "SmokeFree",
  CableReady = "CableReady",
  SatelliteTV = "SatelliteTV",
  DoubleVanities = "DoubleVanities",
  TubShower = "TubShower",
  Intercom = "Intercom",
  SprinklerSystem = "SprinklerSystem",
  RecentlyRenovated = "RecentlyRenovated",
  CloseToTransit = "CloseToTransit",
  GreatView = "GreatView",
  QuietNeighborhood = "QuietNeighborhood",
}

// Add this constant at the end of the file
export const NAVBAR_HEIGHT = 52; // in pixels

export interface PropertyCategory {
  id: number;
  name: string;
  subcategories?: PropertyCategory[];
  sub?: string;
  icon?: string;
}

export const proprtType: PropertyCategory[] = [
  {
    id: 1,
    name: "Commercial",
    sub: "Offices, factories, stores",
    icon: "Building2",
  },
  {
    id: 2,
    name: "Residential",
    sub: "Homes, apartments, villas",
    icon: "Home",
  },
];

export const subCategories: Record<string, PropertyCategory[]> = {};

export const propertyHierarchy: Record<string, PropertyCategory[]> = {
  commercial: [
    // { id: 3, name: "Company" },
    { id: 4, name: "Project" },
    { id: 5, name: "Goods" },
    { id: 6, name: "Factory" },
    { id: 7, name: "Stock" },
    { id: 8, name: "Brands" },
  ],
  residential: [
    {
      id: 9,
      name: "Service",
      subcategories: [
        { id: 11, name: "Office" },
        { id: 12, name: "Market" },
        { id: 13, name: "Service Building" },
        { id: 14, name: "Service Villa" },
        { id: 15, name: "Land (Service)" },
        { id: 16, name: "Store" },
      ],
    },
    {
      id: 10,
      name: "Living",
      subcategories: [
        { id: 17, name: "Apartment" },
        { id: 18, name: "Villa" },
        { id: 19, name: "Land (Living)" },
        { id: 20, name: "Building" },
        { id: 21, name: "Chalet" },
        { id: 22, name: "Farm" },
        { id: 23, name: "Manors" },
      ],
    },
  ],
};

interface SteppersT {
  name: "rooms" | "toilets" | "min_land_area";
  icon: ForwardRefExoticComponent<
    Omit<Icons.LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  label: string;
  para: string;
  incementAmount: number;
  min: number;
}

export const Steppers: SteppersT[] = [
  {
    name: "rooms",
    icon: Icons.Bed,
    label: "rooms",
    para: "roomsPara",
    incementAmount: 1,
    min: 1,
  },
  {
    name: "toilets",
    icon: Icons.Toilet,
    label: "toilets",
    para: "toiletsPara",
    incementAmount: 1,
    min: 1,
  },
  {
    name: "min_land_area",
    icon: Icons.Map,
    label: "min_land_area",
    para: "areaPara",
    incementAmount: 50,
    min: 40,
  },
];

export const QATAR_CITIES: City[] = [
  {
    id: "1",
    name: "Doha",
    coordinates: { lng: 51.5333, lat: 25.2867 },
    radiusKm: 8.4,
  },
  {
    id: "2",
    name: "Al Rayyan",
    coordinates: { lng: 51.42631597850712, lat: 25.29692992368524 },
    radiusKm: 27.93,
  },
  {
    id: "3",
    name: "Umm Salal",
    coordinates: { lng: 51.3975, lat: 25.46972 },
    radiusKm: 10.07,
  },
  {
    id: "4",
    name: "Al Khawr wa adh Dhakhira",
    coordinates: { lng: 51.5002153015179, lat: 25.685475140513503 },
    radiusKm: 22.69,
  },
  {
    id: "5",
    name: "Al Wakrah",
    coordinates: { lng: 51.61, lat: 25.18 },
    radiusKm: 28.6,
  },
  {
    id: "6",
    name: "Al Daayen",
    coordinates: { lng: 51.45, lat: 25.57778 },
    radiusKm: 9.61,
  },
  {
    id: "7",
    name: "Al Shamal",
    coordinates: { lng: 51.21667, lat: 26.11667 },
    radiusKm: 16.54,
  },
  {
    id: "8",
    name: "Al Shahaniyah",
    coordinates: { lng: 51.20472, lat: 25.37222 },
    radiusKm: 32.45,
  },
];
export const sectors: Sector[] = [
  {
    id: 1,
    name: "Commercial",
    items: [
      // { id: 3, name: "Company", icon: "Building" },
      { id: 4, name: "Project", icon: "ClipboardList" },
      { id: 5, name: "Goods", icon: "Package" },
      { id: 6, name: "Factory", icon: "Settings" },
      { id: 7, name: "Stock", icon: "BarChart3" },
      { id: 8, name: "Brands", icon: "Tag" },
    ],
  },
  {
    id: 2,
    name: "Residential",
    items: [
      {
        id: 9,
        name: "ResServ",
        icon: "Building",
        children: [
          { id: 11, name: "Office", icon: "AccessibilityIcon" },
          { id: 12, name: "Market", icon: "ShoppingCart" },
          { id: 13, name: "Service Building", icon: "Store" },
          { id: 14, name: "Service Villa", icon: "Home" },
          { id: 15, name: "Land (Service)", icon: "Map" },
          // { id: 16, name: "Store", icon: "ShoppingBag" },
        ],
      },
      {
        id: 10,
        name: "LivProp",
        icon: "Home",
        children: [
          { id: 17, name: "Apartment", icon: "Building" },
          { id: 18, name: "Villa", icon: "Home" },
          { id: 19, name: "Land (Living)", icon: "MapPin" },
          { id: 20, name: "Building", icon: "Building" },
          { id: 21, name: "Chalet", icon: "Mountain" },
          { id: 22, name: "Farm", icon: "Tractor" },
          { id: 23, name: "Manors", icon: "Castle" },
        ],
      },
    ],
  },
];

// export const categoryNames: { [key: number]: string } = {
//   1: "Commercial",
//   2: "Residential",
//   3: "Company",
//   4: "Project",
//   5: "Goods",
//   6: "Factory",
//   7: "Stock",
//   8: "Brands",
//   9: "Service",
//   10: "Living",
//   11: "Office",
//   12: "Market",
//   13: "Service Building",
//   14: "Service Villa",
//   15: "Land (Service)",
//   16: "Store",
//   17: "Apartment",
//   18: "Villa",
//   19: "Land (Living)",
//   20: "Building",
//   21: "Chalet",
//   22: "Farm",
//   23: "Manors",
// };

export const sampleImages = [
  {
    id: 1,
    src: "/samples/factory.jpeg",
    alt: "Modern living room with large windows",
  },
  {
    id: 2,
    src: "/samples/office.jpeg",
    alt: "Cozy bedroom with natural lighting",
  },
  {
    id: 3,
    src: "/samples/office2.jpeg",
    alt: "Spacious kitchen with island",
  },
  {
    id: 4,
    src: "/samples/mall.png",
    alt: "Elegant bathroom with marble finishes",
  },
  {
    id: 5,
    src: "/samples/skyscrapers.png",
    alt: "Private balcony with city view",
  },
  {
    id: 6,
    src: "/samples/villa1.png",
    alt: "Home office space",
  },
  {
    id: 7,
    src: "/samples/villa2.png",
    alt: "Building exterior and entrance",
  },
];
